import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, ChevronDown, ArrowUpRight, ArrowRight } from "lucide-react";
import { toast } from "react-toastify";
import { auth, storage } from "../../config/firebase";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { useUser } from "../../contexts/UserContext";
import { useArtist } from "../../contexts/ArtistContext";
import ArtistSelector from "../UI/ArtistSelector";
import {
  FAN_MANAGER_ADMIN_URL,
  FAN_MANAGER_URL,
  MIGRATE_CATALOG_URL,
  MIGRATION_ADMIN_URL,
  NEBULA_INSIGHTS_ENABLED,
  NOVADISTRO_URL,
  NOVADISTRO_VAULT_URL,
} from "../../config/featureFlags";
import { getUserDetails } from "../../services/api/user.service";
import { getDistroToken } from "../../distro/lib/distroToken";


interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle?: () => void;
  isDesktop?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, onToggle, isDesktop = false }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const user = auth.currentUser;
  
  // State for handling image loading errors
  const [imageError, setImageError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { userProfile } = useUser();
  const { hasPermission, hasAnyPermission, currentArtistId, availableArtists, currentPermissions, loadingPermissions, loadingTeams, myTeams, isInitialized, switchingArtist } = useArtist();
  const hasTeams = useMemo(() => (myTeams?.length || 0) > 0, [myTeams]);
  const [profilePicUrl, setProfilePicUrl] = useState<string | undefined>(undefined);
  const [ownerUsername, setOwnerUsername] = useState<string | null>(null);

  // Determine if categories should be rendered based on artist selection and permissions
  // Categories are ONLY displayed when an artist is selected AND permissions are loaded
  const shouldRenderCategories = useMemo(() => {
    // Case 1: Teams are still loading - wait
    if (loadingTeams) {
      return false;
    }

    // Case 2: Not initialized yet - wait for full initialization
    if (!isInitialized) {
      return false;
    }

    // Case 3: No artist selected - don't render categories, show error message
    if (!currentArtistId) {
      return false; // Don't show categories without artist selection
    }

    // Case 4: Artist selected but permissions are loading - wait for /my-permissions
    if (loadingPermissions || switchingArtist) {
      return false; // Don't render categories until permissions are loaded or artist switch completes
    }

    // Case 5: Artist selected and permissions loaded - render categories
    // Ensure permissions have actually been loaded (not just initial empty state)
    // We check isInitialized AND that we're not switching to ensure the full flow is complete
    // Also ensure availableArtists is populated (teams have been loaded)
    if (Array.isArray(currentPermissions) && isInitialized && availableArtists.length > 0) {
      return true; // Render categories based on loaded permissions
    }

    // Case 6: Fallback - don't render if permissions array doesn't exist
    return false;
  }, [loadingTeams, loadingPermissions, currentArtistId, currentPermissions, isInitialized, switchingArtist, availableArtists.length]);
  
  // Track the last profile_pic path and resolved URL to prevent unnecessary updates
  const lastProfilePicPathRef = useRef<string>("");
  const lastResolvedUrlRef = useRef<string | undefined>(undefined);
  const failedImageUrlsRef = useRef<Set<string>>(new Set());
  const previousImageUrlRef = useRef<string | undefined>(undefined);
  
  // State for ADMIN dropdown - keep open if on admin pages
  const [isAdminOpen, setIsAdminOpen] = useState(
    currentPath === "/customer-details" || currentPath === "/users-list" || 
    currentPath === "/artist-mapping" || currentPath.startsWith("/admin/")
  );
  
  const [isCatalogOpen, setIsCatalogOpen] = useState(
    currentPath === "/distribution"
  );
  const [isToolboxOpen, setIsToolboxOpen] = useState(false);

  // State for PROFILE dropdown - keep open if on any profile submenu page
  const [isProfileOpen, setIsProfileOpen] = useState(
    currentPath.startsWith("/profile") || currentPath.startsWith("/teams") ||
    currentPath === "/coming-soon/file-cabinet"
  );
  
  // Update dropdown state when path changes
  useEffect(() => {
    // setIsCustomerDetailsOpen(currentPath === "/customer-details" || currentPath === "/users-list");
    setIsCatalogOpen(currentPath === "/distribution");
    setIsProfileOpen(
      currentPath.startsWith("/profile") || currentPath.startsWith("/teams") ||
      currentPath === "/coming-soon/file-cabinet"
    );
    setIsAdminOpen(
      currentPath === "/customer-details" || currentPath === "/users-list" || 
      currentPath === "/artist-mapping" || currentPath.startsWith("/admin/")
    );
  }, [currentPath]);
  
  // Get systemRole from sessionStorage or userProfile
  const getSystemRole = (): string | null => {
    try {
      // First check sessionStorage
      const systemRole = sessionStorage.getItem('systemRole');
      if (systemRole) return systemRole;
      // Fallback to userProfile if available
      if (userProfile?.systemRole) {
        sessionStorage.setItem('systemRole', userProfile.systemRole);
        return userProfile.systemRole;
      }
      return null;
    } catch {
      return null;
    }
  };

  const systemRole = getSystemRole();
  const isSystemAdmin = systemRole === "system_admin";

  // Track the external Distro destination while its token is being fetched.
  const [openingDistroTarget, setOpeningDistroTarget] = useState<string | null>(null);

  /**
   * Open Nova Distro: fetch a Nova-issued token (scoped to the selected artist)
   * and hand it to the external app via the URL fragment (#t=<jwt>).
   */
  const openNovaDistro = async (targetLabel: string, targetUrl: string) => {
    if (openingDistroTarget) return;
    setOpeningDistroTarget(targetLabel);
    try {
      const token = await getDistroToken();
      window.open(
        `${targetUrl}#t=${encodeURIComponent(token)}`,
        "_blank",
        "noopener,noreferrer"
      );
      if (!isDesktop) onClose();
    } catch (error) {
      console.error(`[NovaDistro] Failed to open ${targetLabel}:`, error);
      toast.error(`Couldn't open ${targetLabel}. Please select an artist and try again.`);
    } finally {
      setOpeningDistroTarget(null);
    }
  };

  // Check if the selected artist is the logged-in person (owner)
  const selectedArtist = currentArtistId 
    ? availableArtists.find(a => a.artistId === currentArtistId)
    : null;
  const isSelectedArtistOwner = selectedArtist?.isOwner === true;
  
  // State for artist image URL from localStorage
  const [artistImageUrl, setArtistImageUrl] = useState<string | undefined>(undefined);
  
  // Listen for localStorage changes and initial load
  useEffect(() => {
    // Get initial value
    const initialImageUrl = localStorage.getItem('artistImageUrl') || undefined;
    setArtistImageUrl(initialImageUrl);
    
    // Listen for storage events (when localStorage is updated from other tabs/components)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'artistImageUrl') {
        setArtistImageUrl(e.newValue || undefined);
      }
    };
    
    // Listen for custom events (when localStorage is updated in same tab)
    const handleCustomStorageChange = (e: CustomEvent) => {
      if (e.detail?.key === 'artistImageUrl') {
        setArtistImageUrl(e.detail?.value || undefined);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleCustomStorageChange as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange as EventListener);
    };
  }, []);
  
  // Reset image error state when user or artist image changes (only if URL actually changed)
  useEffect(() => {
    const currentImageUrl = profilePicUrl || user?.photoURL || artistImageUrl;
    
    // Only reset if the image URL actually changed
    if (currentImageUrl !== previousImageUrlRef.current) {
      previousImageUrlRef.current = currentImageUrl;
      
      // Don't reset error if this URL has already failed
      if (!currentImageUrl || !failedImageUrlsRef.current.has(currentImageUrl)) {
        setImageError(false);
        setRetryCount(0);
      } else {
        // URL has failed before, keep error state
        setImageError(true);
        setRetryCount(3);
      }
      
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }
  }, [user?.photoURL, artistImageUrl, profilePicUrl]);

  // Resolve profile picture from users.profile_pic (storage path)
  useEffect(() => {
    const path = userProfile?.profile_pic || "";
    
    // If path hasn't changed, don't re-process
    if (path === lastProfilePicPathRef.current) {
      return;
    }
    
    lastProfilePicPathRef.current = path;
    
    if (!path) {
      // Only update if we had a URL before
      if (lastResolvedUrlRef.current !== undefined) {
        setProfilePicUrl(undefined);
        lastResolvedUrlRef.current = undefined;
      }
      return;
    }
    
    // If a full URL was stored (legacy), use as-is; else resolve from storage path
    if (path.startsWith('http://') || path.startsWith('https://')) {
      // Only update if the URL actually changed
      if (lastResolvedUrlRef.current !== path) {
        setProfilePicUrl(path);
        lastResolvedUrlRef.current = path;
      }
      return;
    }
    
    try {
      const ref = storageRef(storage, path);
      getDownloadURL(ref)
        .then(url => {
          // Only update if the URL actually changed
          if (lastResolvedUrlRef.current !== url) {
            setProfilePicUrl(url);
            lastResolvedUrlRef.current = url;
          }
        })
        .catch((error) => {
          console.warn('[Sidebar] Failed to get download URL for profile pic:', error);
          if (lastResolvedUrlRef.current !== undefined) {
            setProfilePicUrl(undefined);
            lastResolvedUrlRef.current = undefined;
          }
        });
    } catch (error) {
      console.warn('[Sidebar] Error creating storage ref for profile pic:', error);
      if (lastResolvedUrlRef.current !== undefined) {
        setProfilePicUrl(undefined);
        lastResolvedUrlRef.current = undefined;
      }
    }
  }, [userProfile?.profile_pic]);
  
  // Fetch owner's username when artist changes
  useEffect(() => {
    const fetchOwnerUsername = async () => {
      // Reset immediately
      setOwnerUsername(null);
      
      if (!currentArtistId || availableArtists.length === 0) {
        return;
      }

      const selectedArtist = availableArtists.find(a => a.artistId === currentArtistId);
      if (!selectedArtist || !selectedArtist.teams || selectedArtist.teams.length === 0) {
        return;
      }

      const team = selectedArtist.teams[0];
      
      // Try ownerUsername from API response first
      if (team.ownerUsername) {
        setOwnerUsername(team.ownerUsername);
        return;
      }

      // Fetch using ownerId
      if (team.ownerId) {
        try {
          const userDetails = await getUserDetails(team.ownerId);
          if (userDetails?.username) {
            setOwnerUsername(userDetails.username);
          }
        } catch (error) {
          console.error('Error fetching owner username:', error);
        }
      }
    };

    fetchOwnerUsername();
  }, [currentArtistId, availableArtists]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);
  
  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const imgElement = event.currentTarget;
    const failedUrl = imgElement.src;
    
    // If this URL has already failed, don't retry
    if (failedImageUrlsRef.current.has(failedUrl)) {
      setImageError(true);
      setRetryCount(3);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      return;
    }
    
    if (retryCount < 3) {
      // Clear any existing timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, retryCount) * 1000;
      retryTimeoutRef.current = setTimeout(() => {
        const newRetryCount = retryCount + 1;
        if (newRetryCount >= 3) {
          // Mark URL as failed after 3 retries
          failedImageUrlsRef.current.add(failedUrl);
          setImageError(true);
        } else {
          setImageError(false); // Reset error to retry
        }
        setRetryCount(newRetryCount);
      }, delay);
    } else {
      // Give up after 3 retries - mark URL as failed
      failedImageUrlsRef.current.add(failedUrl);
      setImageError(true);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      console.warn('[Sidebar] Image failed to load after 3 retries, marking as failed:', failedUrl);
    }
  };

  // const handleCustomerServiceClick = () => {
  //   if (window.zE) {
  //     // Prefill name & email
  //     // if (user) {
  //     //   window.zE('messenger', 'prefill', {
  //     //     name: { value: user.displayName || '', readOnly: true },
  //     //     email: { value: user.email || '', readOnly: true }
  //     //   });
  //     // }
  //     window.zE('messenger', 'show');
  //     window.zE('messenger', 'open');
  //   }
  // };

  return (
    <>
      {/* ✅ Mobile Toggle Button - Hamburger */}
      {!isDesktop && !isOpen && (
        <button
          className="fixed top-2 z-50 h-10 text-black p-2 md:hidden"
          onClick={onToggle}
          aria-label="Open Sidebar"
        >
          <Menu size={24} />
        </button>
      )}

      {/* ✅ Mobile Backdrop */}
      {!isDesktop && isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={onClose}
          aria-label="Close Sidebar"
        />
      )}

      {/* ✅ Sidebar */}
      <aside className={`fixed top-0 left-0 bg-nova-color text-black border-r border-gray-200 z-40 flex flex-col min-h-0 overflow-hidden md:relative md:w-[22rem] ${
        isOpen || isDesktop ? 'translate-x-0' : '-translate-x-full'
      } transition-transform duration-300 ease-in-out`} style={{ zIndex: 9999, height: "calc(100vh - var(--nova-navbar-height, 0px))", maxHeight: "calc(100vh - var(--nova-navbar-height, 0px))" }}>
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden px-8 py-6 w-[75%] max-w-full" style={{marginLeft: '1.8rem'}}>
          {/* ✅ Profile Section */}
          <div className="flex items-center mb-8 flex-shrink-0">
            <Link to="/profile/account" className="flex items-center cursor-pointer flex-shrink-0">
              {/* Profile Picture */}
              <div className="w-12 h-12 rounded-full bg-gray-300 mr-3 flex items-center justify-center flex-shrink-0">
                {(() => {
                  // Determine which image to show based on team membership
                  let imageToShow: string | undefined;
                  let fallbackInitial: string = 'U';
                  
                  // If user has no team members (no available artists), show their own profile pic
                  if (availableArtists.length === 0) {
                    imageToShow = profilePicUrl || user?.photoURL || undefined;
                    fallbackInitial = user?.displayName?.charAt(0) || user?.email?.charAt(0) || userProfile?.username?.charAt(0) || 'U';
                  } else {
                    // If user has team members, check if they're the owner
                    const selectedArtist = currentArtistId
                      ? availableArtists.find(a => a.artistId === currentArtistId)
                      : null;
                    
                    if (selectedArtist) {
                      // If current user is the owner, show their profile pic
                      if (selectedArtist.isOwner) {
                        imageToShow = profilePicUrl || user?.photoURL || undefined;
                        fallbackInitial = user?.displayName?.charAt(0) || user?.email?.charAt(0) || userProfile?.username?.charAt(0) || 'U';
                      } else {
                        // If not owner, show artist image
                        imageToShow = artistImageUrl || undefined;
                        fallbackInitial = selectedArtist.artistName?.charAt(0) || 'A';
                      }
                    } else {
                      // Fallback: show user's profile pic
                      imageToShow = profilePicUrl || user?.photoURL || undefined;
                      fallbackInitial = user?.displayName?.charAt(0) || user?.email?.charAt(0) || userProfile?.username?.charAt(0) || 'U';
                    }
                  }
                  
                  // Check if this URL has failed before
                  const hasFailed = imageToShow ? failedImageUrlsRef.current.has(imageToShow) : false;
                  const shouldShowImage = imageToShow && !imageError && !hasFailed;
                  
                  return shouldShowImage ? (
                    <img 
                      src={imageToShow} 
                      alt="Profile" 
                      className="w-12 h-12 rounded-full object-cover"
                      onError={handleImageError}
                      onLoad={() => {
                        // On successful load, remove from failed set and reset error state
                        if (imageToShow && failedImageUrlsRef.current.has(imageToShow)) {
                          failedImageUrlsRef.current.delete(imageToShow);
                        }
                        if (imageError || retryCount > 0) {
                          setImageError(false);
                          setRetryCount(0);
                          if (retryTimeoutRef.current) {
                            clearTimeout(retryTimeoutRef.current);
                            retryTimeoutRef.current = null;
                          }
                        }
                      }}
                      key={imageToShow} // Force re-mount when URL changes
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-400 flex items-center justify-center text-white font-semibold">
                      {fallbackInitial}
                    </div>
                  );
                })()}
              </div>
            </Link>
            
            {/* Artist Selector (replaces username) - Outside Link to prevent navigation */}
            <div className="flex-1 min-w-0 max-w-full overflow-hidden">
                <ArtistSelector />
            </div>

            {/* Close Button (Mobile only) */}
            {!isDesktop && (
              <button 
                className="text-gray-600 md:hidden ml-2 flex-shrink-0" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }} 
                aria-label="Close Sidebar"
              >
                <X size={20} />
              </button>
            )}
          </div>
          
          {/* ✅ Navigation Links */}
          {/* [
              "Dashboard",
              "Distribution",
              
              "Store",
              "Tickets",
              "Nebula",
              "Banking",
              "Investments",
              "Lending",
              "Accounting",
              "Settings",
              "Support"
            ] */}
          <div className="nova-sidebar-scroll flex flex-col space-y-1 flex-1 min-h-0 overflow-y-auto overscroll-y-contain pb-6">
            {!shouldRenderCategories ? (
              // Show loading state or error message
              <div className="px-4 py-3 text-xs">
                {loadingTeams || !isInitialized ? (
                  // Show empty while teams are loading (from page load to API response)
                  // Don't show "No Artist Selected" until API response is received and context is initialized
                  <div></div>
                ) : loadingPermissions ? (
                  <div className="text-gray-500">Loading permissions...</div>
                ) : (!hasTeams || !currentArtistId) ? (
                  // Show "No Artist Selected" only after teams API response is received and context is initialized
                  // Check if user has no teams OR no artist selected
                  <div className="text-red-600 font-medium">
                    <div className="mb-1">⚠️ No Artist Selected</div>
                    <div className="text-[0.65rem] text-gray-600 font-normal">
                      Please select an artist to view categories
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">Loading...</div>
                )}
              </div>
            ) : (
              (() => {
                const hasTeams = myTeams && myTeams.length > 0;
                type NavItem = { name: string; permissions: string[] };
                const baseItems: NavItem[] = [
                  { name: "DASHBOARD", permissions: ["analytics:view"] },
                ];

                // If user has no teams, show none here (Profile is handled separately via dropdown).
                const limitedItems: NavItem[] = [];

                const itemsToRender = hasTeams ? baseItems : limitedItems;

                return itemsToRender
                  .filter((item) => {
                    // Show item if:
                    // 1. No permission required (empty permissions array) - but only if artist is selected
                    // 2. Any permission in the list is granted (artist must be selected)
                    // Note: currentArtistId is guaranteed to exist here because shouldRenderCategories requires it
                    if (!item.permissions || item.permissions.length === 0) return true;
                    return item.permissions.some((perm) => hasPermission(perm));
                  })
                  .map((item) => {
                    const itemName = item.name;
                    const itemPath = `/${itemName.toLowerCase().replace(/ /g, "-")}`;
                    const isActive = currentPath === itemPath;
                    const grayedOutItems = ["Store", "Tickets", "Investments", "Accounting"];
                    const isGrayedOut = grayedOutItems.includes(itemName);
                
                    const commonClasses = `flex items-center justify-between py-3 px-0 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                      isActive 
                        ? "text-black-600 bg-gray-100 rounded-lg px-4 py-4" 
                        : "text-black-600 px-4 py-4"
                    }`;
                
                    const textClasses = isGrayedOut ? "opacity-30 cursor-not-allowed sidebar-menu-item" : "sidebar-menu-item";
                    
                    const textHtml = <span className="inline-block">{itemName}</span>;
                    const arrowIcon = isActive 
                      ? <ArrowUpRight size={12} className="text-black-900" />
                      : <ArrowRight size={12} className="text-black-900" style={{opacity: 0.3}} />;
                    return isGrayedOut ? (
                      <div key={itemName} className={commonClasses}>
                        <span className={textClasses}>{textHtml}</span>
                        {arrowIcon}
                      </div>
                    ) : itemName.toLowerCase() === "distribution" ? (
                      <a
                        key={itemName}
                        href="https://distribution.marsmarkets.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={commonClasses}
                        onClick={!isDesktop ? onClose : undefined}
                      >
                        <span className={textClasses} >{textHtml}</span>
                        {arrowIcon}
                      </a>
                    ) : (
                      <Link
                        key={itemName}
                        to={itemPath}
                        className={commonClasses}
                        onClick={!isDesktop ? onClose : undefined}
                      >
                        <span className={textClasses}>{textHtml}</span>
                        {arrowIcon}
                      </Link>
                    );
                  });
              })()
            )}

            {/* Catalog, Marketing, and Banking */}
            {shouldRenderCategories && (
              <>
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => setIsCatalogOpen(!isCatalogOpen)}
                    className={`flex items-center justify-between py-3 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                      currentPath === "/distribution"
                        ? "bg-gray-100 rounded-lg text-black-600"
                        : "text-black-600"
                    }`}
                  >
                    <span className="sidebar-menu-item">CATALOG</span>
                    {isCatalogOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} className="opacity-30" />}
                  </button>
                  {isCatalogOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {hasAnyPermission(["distribution:view", "distribution:manage"]) && (
                        <a
                          href="https://distribution.marsmarkets.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={!isDesktop ? onClose : undefined}
                          className="flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium text-black-600 transition hover:bg-gray-100 hover:rounded-lg"
                        >
                          <span className="sidebar-submenu-item">Distribution</span>
                          <ArrowUpRight size={12} className="opacity-30" />
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => openNovaDistro("Migrate Catalog", MIGRATE_CATALOG_URL)}
                        disabled={openingDistroTarget !== null}
                        className="flex w-full items-center justify-between px-4 py-2 text-[0.7rem] font-medium text-black-600 transition hover:bg-gray-100 hover:rounded-lg disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="sidebar-submenu-item">
                          {openingDistroTarget === "Migrate Catalog" ? "Opening…" : "Migrate Catalog"}
                        </span>
                        <ArrowUpRight size={12} className="opacity-30" />
                      </button>
                      <button
                        type="button"
                        onClick={() => openNovaDistro("Vault", NOVADISTRO_VAULT_URL)}
                        disabled={openingDistroTarget !== null}
                        className="flex w-full items-center justify-between px-4 py-2 text-[0.7rem] font-medium text-black-600 transition hover:bg-gray-100 hover:rounded-lg disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="sidebar-submenu-item">
                          {openingDistroTarget === "Vault" ? "Opening…" : "Vault"}
                        </span>
                        <ArrowUpRight size={12} className="opacity-30" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => setIsToolboxOpen(!isToolboxOpen)}
                    className="flex items-center justify-between px-4 py-3 text-[0.7rem] font-medium text-black-600 transition hover:bg-gray-100 hover:rounded-lg"
                  >
                    <span className="sidebar-menu-item">MARKETING</span>
                    {isToolboxOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} className="opacity-30" />}
                  </button>
                  {isToolboxOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {[
                        { label: "My Links", url: NOVADISTRO_URL },
                        { label: "Campaigns", url: FAN_MANAGER_URL },
                      ].map(({ label, url }) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => openNovaDistro(label, url)}
                          disabled={openingDistroTarget !== null}
                          className="flex w-full items-center justify-between px-4 py-2 text-[0.7rem] font-medium text-black-600 transition hover:bg-gray-100 hover:rounded-lg disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="sidebar-submenu-item">
                            {openingDistroTarget === label ? "Opening…" : label}
                          </span>
                          <ArrowUpRight size={12} className="opacity-30" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {hasAnyPermission(["banking:view", "banking:manage"]) && (
                  <Link
                    to="/banking"
                    onClick={!isDesktop ? onClose : undefined}
                    className={`flex items-center justify-between px-4 py-3 text-[0.7rem] font-medium transition hover:bg-gray-100 hover:rounded-lg ${
                      currentPath === "/banking" ? "bg-gray-100 rounded-lg text-black-600" : "text-black-600"
                    }`}
                  >
                    <span className="sidebar-menu-item">BANKING</span>
                    {currentPath === "/banking" ? <ArrowUpRight size={12} /> : <ArrowRight size={12} className="opacity-30" />}
                  </Link>
                )}
              </>
            )}

            {shouldRenderCategories && hasAnyPermission(["nebula:view", "nebula:manage"]) && (
              <Link
                to="/nebula"
                onClick={!isDesktop ? onClose : undefined}
                className={`flex items-center justify-between px-4 py-3 text-[0.7rem] font-medium transition hover:bg-gray-100 hover:rounded-lg ${
                  currentPath === "/nebula" ? "bg-gray-100 rounded-lg text-black-600" : "text-black-600"
                }`}
              >
                <span className="sidebar-menu-item">NEBULA</span>
                {currentPath === "/nebula" ? <ArrowUpRight size={12} /> : <ArrowRight size={12} className="opacity-30" />}
              </Link>
            )}

            {/* PROFILE with Dropdown - Only render after artist selection and permissions are loaded */}
            {shouldRenderCategories && (hasAnyPermission(["profile:view", "profile:edit"]) || hasTeams) && (
              <div className="flex flex-col">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className={`flex items-center justify-between py-3 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                    currentPath.startsWith("/profile") || currentPath.startsWith("/teams") ||
                    currentPath === "/coming-soon/file-cabinet"
                      ? "text-black-600 bg-gray-100 rounded-lg"
                      : "text-black-600"
                  }`}
                >
                  <span className="sidebar-menu-item">PROFILE</span>
                  {isProfileOpen ? (
                    <ChevronDown size={12} className="text-black-900" />
                  ) : (
                    <ChevronRight size={12} className="text-black-900" style={{ opacity: 0.3 }} />
                  )}
                </button>

                {isProfileOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    <Link
                      to="/profile/account"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/profile/account"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Account Information</span>
                      {currentPath === "/profile/account" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>

                    <Link
                      to="/profile/change-password"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/profile/change-password"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Change Password</span>
                      {currentPath === "/profile/change-password" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>

                    <Link
                      to="/profile/artist"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/profile/artist"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Artist Information</span>
                      {currentPath === "/profile/artist" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>

                    <Link
                      to="/coming-soon/file-cabinet"
                      onClick={!isDesktop ? onClose : undefined}
                      className={`flex items-center justify-between px-4 py-2 text-[0.7rem] font-medium transition hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/coming-soon/file-cabinet" ? "bg-gray-100 rounded-lg text-black-600" : "text-black-600"
                      }`}
                    >
                      <span className="sidebar-submenu-item">File Cabinet</span>
                      {currentPath === "/coming-soon/file-cabinet" ? <ArrowUpRight size={12} /> : <ArrowRight size={12} className="opacity-30" />}
                    </Link>

                    {hasTeams && hasAnyPermission(["team:view", "team:invite", "team:remove", "team:assign_roles", "team:manage"]) && (
                      <Link
                        to="/teams"
                        className={`flex items-center justify-between px-4 py-2 text-[0.7rem] font-medium transition hover:bg-gray-100 hover:rounded-lg ${
                          currentPath.startsWith("/teams") ? "bg-gray-100 rounded-lg text-black-600" : "text-black-600"
                        }`}
                        onClick={!isDesktop ? onClose : undefined}
                      >
                        <span className="sidebar-submenu-item">Teams</span>
                        {currentPath.startsWith("/teams") && <ArrowUpRight size={12} />}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            {shouldRenderCategories && (
              <>
                <Link
                  to="/store/beat-shop"
                  onClick={!isDesktop ? onClose : undefined}
                  className={`flex items-center justify-between px-4 py-3 text-[0.7rem] font-medium transition hover:bg-gray-100 hover:rounded-lg ${
                    currentPath.startsWith("/store/beat-shop") ? "bg-gray-100 rounded-lg text-black-600" : "text-black-600"
                  }`}
                >
                  <span className="sidebar-menu-item">BEAT SHOP</span>
                  {currentPath.startsWith("/store/beat-shop") ? <ArrowUpRight size={12} /> : <ArrowRight size={12} className="opacity-30" />}
                </Link>
                {hasAnyPermission(["support:view", "support:manage", "social:view", "social:manage"]) && (
                  <Link
                    to="/customer-service"
                    onClick={!isDesktop ? onClose : undefined}
                    className={`flex items-center justify-between px-4 py-3 text-[0.7rem] font-medium transition hover:bg-gray-100 hover:rounded-lg ${
                      currentPath === "/customer-service" ? "bg-gray-100 rounded-lg text-black-600" : "text-black-600"
                    }`}
                  >
                    <span className="sidebar-menu-item">CUSTOMER SERVICE</span>
                    {currentPath === "/customer-service" ? <ArrowUpRight size={12} /> : <ArrowRight size={12} className="opacity-30" />}
                  </Link>
                )}
              </>
            )}
            
            {/* PAYOUTS with Dropdown - Only render for system_admin users when viewing their own artist profile - COMMENTED OUT */}
            {/* {shouldRenderCategories && isSystemAdmin && isSelectedArtistOwner && (
              <div className="flex flex-col">
                <button
                  onClick={() => setIsPayoutsOpen(!isPayoutsOpen)}
                  className={`flex items-center justify-between py-3 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                    currentPath.startsWith("/payouts")
                      ? "text-black-600 bg-gray-100 rounded-lg"
                      : "text-black-600"
                  }`}
                >
                  <span className="sidebar-menu-item">PAYOUTS</span>
                  {isPayoutsOpen ? (
                    <ChevronDown size={12} className="text-black-900" />
                  ) : (
                    <ChevronRight size={12} className="text-black-900" style={{opacity: 0.3}} />
                  )}
                </button>
                
                {isPayoutsOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    <Link
                      to="/payouts/dashboard"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/payouts/dashboard"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Dashboard</span>
                      {currentPath === "/payouts/dashboard" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                    <Link
                      to="/payouts/queue"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/payouts/queue"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Queue Status</span>
                      {currentPath === "/payouts/queue" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                    <Link
                      to="/payouts/ledger"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/payouts/ledger"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Ledger</span>
                      {currentPath === "/payouts/ledger" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                  </div>
                )}
              </div>
            )} */}
            
            
            {/* ADMIN with Dropdown - Only render after artist selection and permissions are loaded */}
            {/* Show admin category only if user is system admin AND selected artist is the logged-in person (owner) */}
            {shouldRenderCategories && isSystemAdmin && isSelectedArtistOwner && hasPermission("team:manage") && (
              <div className="flex flex-col">
                <button
                  onClick={() => setIsAdminOpen(!isAdminOpen)}
                  className={`flex items-center justify-between py-3 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                    currentPath === "/customer-details" || currentPath === "/users-list" || 
                    currentPath === "/artist-mapping" || currentPath.startsWith("/admin/")
                      ? "text-black-600 bg-gray-100 rounded-lg"
                      : "text-black-600"
                  }`}
                >
                  <span className="sidebar-menu-item">ADMIN</span>
                  {isAdminOpen ? (
                    <ChevronDown size={12} className="text-black-900" />
                  ) : (
                    <ChevronRight size={12} className="text-black-900" style={{opacity: 0.3}} />
                  )}
                </button>
                
                {/* Dropdown Menu */}
                {isAdminOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    <Link
                      to="/customer-details"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/customer-details"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Bank Accounts Details</span>
                      {currentPath === "/customer-details" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                    <Link
                      to="/users-list"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/users-list"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Users list</span>
                      {currentPath === "/users-list" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                    <Link
                      to="/admin/account-requests"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/admin/account-requests"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">User account requests</span>
                      {currentPath === "/admin/account-requests" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                    <Link
                      to="/artist-mapping"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/artist-mapping"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Artist Mapping</span>
                      {currentPath === "/artist-mapping" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                    {NEBULA_INSIGHTS_ENABLED && (
                    <Link
                      to="/admin/nebula-insights"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/admin/nebula-insights"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Nebula Insights</span>
                      {currentPath === "/admin/nebula-insights" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                    )}
                    <Link
                      to="/admin/beat-shop"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/admin/beat-shop"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Beat Shop</span>
                      {currentPath === "/admin/beat-shop" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                    <Link
                      to="/admin/roles"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/admin/roles"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Roles</span>
                      {currentPath === "/admin/roles" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                    <a
                      href="https://mylinks.novadistro.io/nova-admin"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg text-black-600"
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">MyLinks Admin</span>
                    </a>
                    <button
                      type="button"
                      onClick={() => openNovaDistro("Migration Admin", MIGRATION_ADMIN_URL)}
                      disabled={openingDistroTarget !== null}
                      className="flex w-full items-center py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg text-black-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <span className="sidebar-submenu-item">
                        {openingDistroTarget === "Migration Admin" ? "Opening…" : "Migration Admin"}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => openNovaDistro("FanManager Admin", FAN_MANAGER_ADMIN_URL)}
                      disabled={openingDistroTarget !== null}
                      className="flex w-full items-center py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg text-black-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <span className="sidebar-submenu-item">
                        {openingDistroTarget === "FanManager Admin" ? "Opening…" : "FanManager Admin"}
                      </span>
                    </button>
                    <Link
                      to="/admin/surveys"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/admin/surveys"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Surveys</span>
                      {currentPath === "/admin/surveys" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                    <Link
                      to="/admin/announcements"
                      className={`flex items-center justify-between py-2 px-4 text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 hover:rounded-lg ${
                        currentPath === "/admin/announcements"
                          ? "text-black-600 bg-gray-100 rounded-lg"
                          : "text-black-600"
                      }`}
                      onClick={!isDesktop ? onClose : undefined}
                    >
                      <span className="sidebar-submenu-item">Announcements</span>
                      {currentPath === "/admin/announcements" && (
                        <ArrowUpRight size={12} className="text-black-900" />
                      )}
                    </Link>
                  </div>
                )}
              </div>
            )}

          </div>
          
          {/* ✅ Settings Button */}
          {/* <div className="mt-auto pt-6">
            <Link
              to="/settings"
              className={`flex items-center justify-center w-full py-3 px-4 rounded-lg-settings text-[0.7rem] font-medium transition-all duration-500 ease-in-out hover:bg-gray-100 sidebar-menu-item ${
                currentPath === '/settings'
                  ? "border border-gray-200 text-black-600 bg-gray-100"
                  : "border border-gray-200 text-black-600 hover:bg-gray-100"
              }`}
              onClick={!isDesktop ? onClose : undefined}
            >
              SETTINGS
            </Link>
          </div> */}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
